import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/shadcn/table'
import { Badge } from '@/components/shadcn/badge'
import { Button } from '@/components/shadcn/button'
import { Pencil, Trash2, Boxes, Download } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { EmployeeUserResponse, OrderType } from '@/api/model'
import { AccountStatus, PermissionEntityType } from '@/api/model'
import { SortableTableHead } from '@/components/common/SortableTableHead'
import { LoaderTable } from '@/components/common/loaders/LoaderTable'
import { usePermissions } from '@/hooks/usePermissions'

export const EmployeeSortFields = {
  FirstName: 'FirstName',
  LastName: 'LastName',
  EmployeeNumber: 'EmployeeNumber',
  Position: 'Position',
  Status: 'Status',
  BirthDate: 'BirthDate',
  EmploymentDate: 'EmploymentDate',
} as const

function formatDate(value: string | null | undefined) {
  if (!value) return '—'
  return new Date(value).toLocaleDateString('de-DE')
}

interface Props {
  isLoading: boolean
  isError: boolean
  employees: EmployeeUserResponse[] | undefined | null
  currentSort?: string
  currentOrder?: OrderType
  onSort: (sort?: string, order?: OrderType) => void
  onEdit: (employee: EmployeeUserResponse) => void
  onDelete: (employee: EmployeeUserResponse) => void
  onModules?: (employee: EmployeeUserResponse) => void
  onRowClick?: (employee: EmployeeUserResponse) => void
}

export function EmployeesDataTable({
  isLoading,
  isError,
  employees,
  currentSort,
  currentOrder,
  onSort,
  onEdit,
  onDelete,
  onModules,
  onRowClick,
}: Props) {
  const { t } = useTranslation('common')
  const { userHasPermission } = usePermissions()
  const canEdit = userHasPermission(PermissionEntityType.Employees, 'Edit')
  const canDelete = userHasPermission(PermissionEntityType.Employees, 'Delete')
  return (
    <Table>
      <TableHeader className="sticky top-0 z-10 bg-card shadow-sm [&_th]:bg-card">
        <TableRow>
          <SortableTableHead field={EmployeeSortFields.FirstName} currentSort={currentSort} currentOrder={currentOrder} onSort={onSort}>
            {t('columns.firstName')}
          </SortableTableHead>
          <SortableTableHead field={EmployeeSortFields.LastName} currentSort={currentSort} currentOrder={currentOrder} onSort={onSort}>
            {t('columns.lastName')}
          </SortableTableHead>
          <SortableTableHead field={EmployeeSortFields.BirthDate} currentSort={currentSort} currentOrder={currentOrder} onSort={onSort}>
            {t('columns.birthDate')}
          </SortableTableHead>
          <SortableTableHead field={EmployeeSortFields.EmploymentDate} currentSort={currentSort} currentOrder={currentOrder} onSort={onSort}>
            {t('columns.employmentDate')}
          </SortableTableHead>
          <SortableTableHead field={EmployeeSortFields.Position} currentSort={currentSort} currentOrder={currentOrder} onSort={onSort}>
            {t('columns.position')}
          </SortableTableHead>
          <SortableTableHead field={EmployeeSortFields.EmployeeNumber} currentSort={currentSort} currentOrder={currentOrder} onSort={onSort}>
            {t('columns.employeeNumber')}
          </SortableTableHead>
          <TableHead>{t('columns.groups')}</TableHead>
          <TableHead>{t('columns.subCompany')}</TableHead>
          <TableHead>{t('columns.modules')}</TableHead>
          <TableHead>{t('columns.registrationCode')}</TableHead>
          <SortableTableHead field={EmployeeSortFields.Status} currentSort={currentSort} currentOrder={currentOrder} onSort={onSort}>
            {t('columns.status')}
          </SortableTableHead>
          <TableHead className="w-25" />
        </TableRow>
      </TableHeader>
      <TableBody>
        <LoaderTable isLoading={isLoading} isError={isError} isEmpty={!employees?.length} colSpan={12}>
          {employees?.map((employee) => (
          <TableRow
            key={employee.id}
            className={onRowClick ? 'cursor-pointer' : undefined}
            onClick={() => onRowClick?.(employee)}
          >
            <TableCell className="font-medium">{employee.firstName || '—'}</TableCell>
            <TableCell className="font-medium">{employee.lastName || '—'}</TableCell>
            <TableCell className="text-muted-foreground">{formatDate(employee.birthDate)}</TableCell>
            <TableCell className="text-muted-foreground">{formatDate(employee.employmentDate)}</TableCell>
            <TableCell className="text-muted-foreground">{employee.position || '—'}</TableCell>
            <TableCell className="text-muted-foreground">{employee.employeeNumber || '—'}</TableCell>
            <TableCell>
              {employee.groups?.length ? (
                <div className="flex flex-wrap gap-1">
                  {employee.groups.map((g) => (
                    <Badge key={g.id} variant="secondary">{g.name}</Badge>
                  ))}
                </div>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </TableCell>
            <TableCell className="text-muted-foreground">{employee.subCompanyName || '—'}</TableCell>
            <TableCell onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="icon" onClick={() => onModules?.(employee)}>
                <Boxes className="size-4" />
              </Button>
            </TableCell>
            <TableCell>
              <div className="flex items-center gap-1">
                {employee.registrationCode ? (
                  <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{employee.registrationCode}</code>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
                {employee.onboardingFileDownloadUrl && (
                  <Button variant="ghost" size="icon" asChild>
                    <a
                      href={employee.onboardingFileDownloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Download className="size-4" />
                    </a>
                  </Button>
                )}
              </div>
            </TableCell>
            <TableCell>
              <Badge variant={employee.status === AccountStatus.Active ? 'default' : 'outline'}>
                {employee.status === AccountStatus.Active ? t('status.active') : t('status.blocked')}
              </Badge>
            </TableCell>
            <TableCell onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" onClick={() => onEdit(employee)} disabled={!canEdit}>
                  <Pencil className="size-4" />
                </Button>
                {canDelete && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-destructive hover:text-destructive"
                    onClick={() => onDelete(employee)}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                )}
              </div>
            </TableCell>
          </TableRow>
          ))}
        </LoaderTable>
      </TableBody>
    </Table>
  )
}
